// RegistryChildrenSuggested.jsx

import React, { useMemo, useState } from 'react';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import SelectInput from 'components/selectinput/SelectInput';
import {
    buildNodeTypeDefinitionMap,
    compareNodeTypeOptionText,
    formatNodeTypeOptionText,
    getNodeTypeDefinitionFromMap,
    getNodeTypePluralLabelFromDefinition,
} from 'utilities/NodeTypeLabels';
import { isRelatedTypeAllowedForHost } from 'utilities/nodeAncestry';
import {
    normalizeRelatedEntries,
    normalizeTypeKey,
    normalizeTypeKeyList,
} from 'utilities/nodeTypeRelatedLists';

/**
 * emptyEditorEntry
 *
 * @returns {{
 *   type: string,
 *   alsoShowTabs: string[],
 *   showInTab: boolean,
 *   showInAttachMenu: boolean,
 * }}
 */
function emptyEditorEntry() {
    return {
        type: '',
        alsoShowTabs: [],
        showInTab: true,
        showInAttachMenu: true,
    };
}

/**
 * toEditorEntry
 *
 * @param {*} entry
 * @returns {{
 *   type: string,
 *   alsoShowTabs: string[],
 *   showInTab: boolean,
 *   showInAttachMenu: boolean,
 * }}
 */
function toEditorEntry(entry) {
    const normalized = normalizeRelatedEntries([entry])[0];
    return normalized || emptyEditorEntry();
}

/**
 * RegistryChildrenSuggested
 *
 * Admin editor for a type's related types (`suggestedChildren` on the type doc).
 * Supports N2 "Also show tabs for…" and per-edge tab/attach visibility.
 *
 * @param {Array} suggestedChildren - Related type entries (string or rich object).
 * @param {object[]} types - Available node type definitions.
 * @param {string} [hostTypeKey] - Type key of the host type being edited.
 * @param {function} onChange - Updates related type entries.
 * @param {boolean} [showTypeKeys=true] - Whether list rows show type keys.
 *
 * @returns {ReactNode} - RegistryChildrenSuggested.
 */
const RegistryChildrenSuggested = React.memo(({
    suggestedChildren,
    types,
    hostTypeKey = '',
    onChange,
    showTypeKeys = true,
}) => {

    // ----- STATE ----- //

    const [selectedIndex, setSelectedIndex] = useState(-1);

    // ----- MEMOS ----- //

    const typeMap = useMemo(() => buildNodeTypeDefinitionMap(types), [types]);
    const draftEntries = useMemo(
        () => (Array.isArray(suggestedChildren) ? suggestedChildren : []).map(toEditorEntry),
        [suggestedChildren],
    );
    const selectedEntry = selectedIndex >= 0 ? draftEntries[selectedIndex] : null;
    const selectedTypeKey = selectedEntry?.type || '';

    const selectedChildRelatedKeys = useMemo(() => {
        if (!selectedTypeKey) return [];
        const childType = getNodeTypeDefinitionFromMap(typeMap, selectedTypeKey);
        return normalizeTypeKeyList(childType?.suggestedChildren);
    }, [selectedTypeKey, typeMap]);

    const usedTypeKeys = useMemo(
        () => new Set(draftEntries.map((entry) => entry.type).filter(Boolean)),
        [draftEntries],
    );

    // ----- HANDLERS ----- //

    const pushChange = (next) => onChange(next);

    const addChildType = () => {
        const next = [...draftEntries, emptyEditorEntry()];
        pushChange(next);
        setSelectedIndex(next.length - 1);
    };

    const removeChildType = () => {
        if (selectedIndex < 0) return;
        const next = draftEntries.filter((_, i) => i !== selectedIndex);
        pushChange(next);
        setSelectedIndex(Math.min(selectedIndex, next.length - 1));
    };

    const moveChildType = (delta) => {
        if (selectedIndex < 0) return;
        const to = selectedIndex + delta;
        if (to < 0 || to >= draftEntries.length) return;
        const next = [...draftEntries];
        const [moved] = next.splice(selectedIndex, 1);
        next.splice(to, 0, moved);
        pushChange(next);
        setSelectedIndex(to);
    };

    const patchSelectedType = (newType) => {
        if (selectedIndex < 0) return;
        const next = [...draftEntries];
        const current = next[selectedIndex] || emptyEditorEntry();
        const nextType = normalizeTypeKey(newType);
        const typeUnchanged = nextType === current.type;
        next[selectedIndex] = {
            type: nextType,
            // Reset N2 picks and visibility when the intermediate type changes.
            alsoShowTabs: typeUnchanged ? current.alsoShowTabs : [],
            showInTab: typeUnchanged ? current.showInTab !== false : true,
            showInAttachMenu: typeUnchanged ? current.showInAttachMenu !== false : true,
        };
        pushChange(next);
    };

    const patchSelectedVisibility = (key, value) => {
        if (selectedIndex < 0 || !selectedEntry) return;
        const next = [...draftEntries];
        next[selectedIndex] = {
            ...selectedEntry,
            [key]: Boolean(value),
        };
        pushChange(next);
    };

    const toggleAlsoShowTab = (tabTypeKey) => {
        if (selectedIndex < 0 || !selectedEntry?.type) return;
        const tabKey = normalizeTypeKey(tabTypeKey);
        if (!tabKey) return;
        const current = new Set(selectedEntry.alsoShowTabs || []);
        if (current.has(tabKey)) current.delete(tabKey);
        else current.add(tabKey);
        const next = [...draftEntries];
        next[selectedIndex] = {
            ...selectedEntry,
            alsoShowTabs: selectedChildRelatedKeys.filter((key) => current.has(key)),
        };
        pushChange(next);
    };

    // ----- RENDER ----- //

    return (

        <div className="registry-children-container">

            <div className="registry-section-title">
                Related Types
            </div>

            <div className="registry-section-hint">
                Related types keep the parent/child relationship. Use the editor checkboxes to
                control whether each type appears as a related tab and/or in Attach / Add menus.
                Order controls how related groups appear on a record. Optionally surface a related
                type&apos;s own related tabs one level down.
            </div>

            <div className="registry-children-toolbar">
                <Button label="Add Related Type" onClick={addChildType} enabled={true} />
                <Button label="Remove" onClick={removeChildType} enabled={selectedIndex >= 0} />
                <Button label="Move Up" onClick={() => moveChildType(-1)} enabled={selectedIndex > 0} />
                <Button
                    label="Move Down"
                    onClick={() => moveChildType(+1)}
                    enabled={selectedIndex >= 0 && selectedIndex < draftEntries.length - 1}
                />
            </div>

            <div className="registry-children-content">

                <div className="registry-children-list">
                    {draftEntries.length === 0 && (
                        <div className="registry-fields-empty">No related types yet.</div>
                    )}

                    {draftEntries.map((entry, i) => {
                        const typeKey = entry.type;
                        const typeObj = getNodeTypeDefinitionFromMap(typeMap, typeKey);
                        const label = typeKey
                            ? getNodeTypePluralLabelFromDefinition(typeObj, typeKey)
                            : '';
                        const n2Count = Array.isArray(entry.alsoShowTabs)
                            ? entry.alsoShowTabs.length
                            : 0;
                        return (
                            <div
                                key={`${typeKey || 'empty'}_${i}`}
                                className={[
                                    'registry-children-row',
                                    !showTypeKeys ? 'registry-children-row-label-only' : '',
                                    i === selectedIndex ? 'selected' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => setSelectedIndex(i)}
                            >
                                {showTypeKeys ? (
                                    <div className="registry-children-row-type">
                                        {typeKey || <span className="registry-muted">(type)</span>}
                                    </div>
                                ) : null}
                                <div className="registry-children-row-label">
                                    {label || <span className="registry-muted">Select a type</span>}
                                    {n2Count > 0 ? (
                                        <span className="registry-children-row-n2">
                                            {` · ${n2Count} nested tab${n2Count === 1 ? '' : 's'}`}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="registry-children-editor">

                    <div className="registry-editor-title">Editor</div>

                    {selectedIndex < 0 ? (
                        <div className="registry-fields-empty">
                            Select an item to edit, or click &quot;Add Related Type&quot;.
                        </div>
                    ) : (
                        <>
                            <div className="registry-field">
                                <div className="registry-labels">Type</div>
                                <SelectInput
                                    placeholder="Select a node type"
                                    value={selectedTypeKey}
                                    options={types
                                        .filter((type) => (
                                            (!usedTypeKeys.has(type.key)
                                              || type.key === selectedTypeKey)
                                            && isRelatedTypeAllowedForHost(
                                              type,
                                              hostTypeKey,
                                              selectedTypeKey,
                                            )
                                        ))
                                        .sort((a, b) => compareNodeTypeOptionText(a, b))
                                        .map((type) => ({
                                            value: type.key,
                                            label: formatNodeTypeOptionText(type),
                                        }))}
                                    onChange={(e) => patchSelectedType(e.target.value)}
                                />
                            </div>

                            {selectedTypeKey ? (
                                <>
                                    <div className="registry-field">
                                        <div className="registry-also-show-list">
                                            <label className="registry-also-show-row">
                                                <Checkbox
                                                    value={selectedEntry?.showInTab !== false}
                                                    onChange={() => patchSelectedVisibility(
                                                        'showInTab',
                                                        selectedEntry?.showInTab === false,
                                                    )}
                                                />
                                                <span className="registry-also-show-label">
                                                    Show as related tab
                                                </span>
                                            </label>
                                            <label className="registry-also-show-row">
                                                <Checkbox
                                                    value={selectedEntry?.showInAttachMenu !== false}
                                                    onChange={() => patchSelectedVisibility(
                                                        'showInAttachMenu',
                                                        selectedEntry?.showInAttachMenu === false,
                                                    )}
                                                />
                                                <span className="registry-also-show-label">
                                                    Show in attach menu
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="registry-field">
                                        <div className="registry-labels">Also show tabs for…</div>
                                        {selectedChildRelatedKeys.length === 0 ? (
                                            <div className="registry-fields-empty">
                                                This type has no related types to surface.
                                            </div>
                                        ) : (
                                            <div className="registry-also-show-list">
                                                {selectedChildRelatedKeys.map((tabTypeKey) => {
                                                    const tabDef = getNodeTypeDefinitionFromMap(
                                                        typeMap,
                                                        tabTypeKey,
                                                    );
                                                    const checked = (selectedEntry?.alsoShowTabs || [])
                                                        .includes(tabTypeKey);
                                                    return (
                                                        <label
                                                            key={tabTypeKey}
                                                            className="registry-also-show-row"
                                                        >
                                                            <Checkbox
                                                                value={checked}
                                                                onChange={() => toggleAlsoShowTab(tabTypeKey)}
                                                            />
                                                            <span className="registry-also-show-label">
                                                                {getNodeTypePluralLabelFromDefinition(
                                                                    tabDef,
                                                                    tabTypeKey,
                                                                )}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
});

RegistryChildrenSuggested.displayName = 'RegistryChildrenSuggested';

export default RegistryChildrenSuggested;
